import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {pluralize} from 'BenchlingUtil';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getAccessibleNonBatchContainerContents} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import SampleStatusTable from 'inventory/sample-status/SampleStatusTable';
import PrintLabelTimezonePicker from 'label-printing/PrintLabelTimezonePicker';
import {PrintPreferences} from 'notebook/mediocre/print/PrintConstants';
import notifications from 'notifications';
import preferences from 'Preferences';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import {NumericInputChangeEvent} from 'util/Types';

import {PrintBarcodeModalQuery} from './__generated/PrintBarcodeModalQuery';
import {BartenderLabelTemplateDropdown, BartenderPrinterDropdown} from './BartenderPrintModalDropdowns';
import BartenderPrintPreview from './BartenderPrintPreview';
import {bartenderPrintItems, BartenderServerLabelTemplate, BartenderServerPrinter} from './BartenderUtil';
import {isPrintableContainer, LabelPrintable} from './LabelPrintableSummary';
import {LabelPrintingMethod} from './LabelPrintingConstants';
import {logPrint} from './LabelPrintingDAO';

type ContainerContent = ExtractByTypename<
  PrintBarcodeModalQuery['storables'][number],
  'ContainerNode'
>['contents'][number];

type ContainedItem = ExcludeInaccessible<ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>>;

type LabelPrintingConfig = PrintBarcodeModalQuery['labelPrintingConfig'];

interface AccessibleContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

interface Props {
  labelPrintingConfig: LabelPrintingConfig;
  onRequestClose: () => void;
  printableItems: Array<LabelPrintable>;
  tenantId: string;
  userId: string;
  analyticsMetadata?: AnalyticsMetadataForContext;
  shouldAlwaysShowSampleStatusTable?: boolean;
  samples: Array<SampleStatusItem> | null;
}

export default function BartenderPrintModal(props: Props): JSX.Element | null {
  const [copies, setCopies] = useState<number | null>(1);
  const [labelTemplate, setLabelTemplate] = useState<BartenderServerLabelTemplate | null>(null);
  const [printer, setPrinter] = useState<BartenderServerPrinter | null>(null);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Array<string>>(
    props.printableItems.map(({id}) => id)
  );

  const trackAnalyticsEvents = (
    itemsToPrint: Array<LabelPrintable>,
    numSuccessfulPrints: number,
    error: unknown
  ): void => {
    const eventGroupId =
      props.analyticsMetadata?.eventGroupId ??
      Analytics.deprecatedGenerateEventGroupId(props.tenantId, props.userId);

    itemsToPrint.forEach((item, i) => {
      const outcome =
        i < numSuccessfulPrints
          ? 'success'
          : i === numSuccessfulPrints
            ? 'failed'
            : 'not_attempted_prior_failure';
      const errorMessage = outcome === 'failed' ? String(error).toString() : null;

      const contents =
        isPrintableContainer(item) && item.contents.length > 0
          ? getAccessibleNonBatchContainerContents<ContainerContent, AccessibleContentWithoutBatches>(
              item.contents
            )
          : null;
      Analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.INITIATED_LABEL_PRINT, {
        ...standardAnalyticsMetadataToDict(props.analyticsMetadata ?? {}),
        event_group_id: eventGroupId,
        print_method: LabelPrintingMethod.BARTENDER,
        printer_server: printer?.bartenderServer.url,
        printer_name: printer?.item,
        label_template_name: labelTemplate?.item.relativePath,
        num_copies: copies,
        item_id: item.id,
        item_type: APIUtil.apiIdToType(item.id),
        // If all contents are inaccessible, the list will be empty
        contained_item_id: contents?.[0]?.containedItem.id,
        contained_item_type: contents?.[0]?.containedItem.id
          ? APIUtil.apiIdToType(contents?.[0]?.containedItem.id)
          : null,
        num_contained_entities: contents?.length,
        outcome,
        error_message: errorMessage,
        // TODO(BNCH-42640) template_fields
        // printer_id, label_template_id -- n/a for BarTender because these are not kept in Benchling db
      });
    });
  };

  const print = (): void => {
    setIsPrinting(true);
    preferences.set(
      `${PrintPreferences.BARTENDER_LABEL_PRINTING_OPTIONS_NAMESPACE}.${props.printableItems[0].schemaId}`,
      {labelTemplate, printer}
    );
    const itemsToPrint = props.printableItems.filter(({id}) => selectedItemIds.includes(id));
    bartenderPrintItems({
      bartenderBaseUrl: printer!.bartenderServer.url,
      printableItemsLabelParams: itemsToPrint.map((printableItem) => printableItem.labelParamsJson),
      labelTemplate: labelTemplate!.item,
      printer: printer!.item,
      copies: copies!,
    }).then(({numSuccessfulPrints, error}) => {
      if (error == null) {
        notifications.success(
          `Printed ${copies} ${pluralize(copies!, 'copy')} ${
            numSuccessfulPrints > 1 ? 'each ' : ''
          }of ${numSuccessfulPrints} ${pluralize(numSuccessfulPrints, 'label')}`
        );
      } else if (numSuccessfulPrints > 0) {
        notifications.error(
          `Error after printing ${copies} ${pluralize(copies!, 'copy')} ${
            numSuccessfulPrints > 1 ? 'each ' : ''
          }of ${numSuccessfulPrints} ${pluralize(numSuccessfulPrints, 'label')}: ${String(error).toString()}`
        );
      } else {
        notifications.error(
          `Error printing ${pluralize(itemsToPrint.length, 'label')}: ${String(error).toString()}`
        );
      }
      trackAnalyticsEvents(itemsToPrint, numSuccessfulPrints, error);
      logPrint(
        itemsToPrint.map((i) => i.id),
        labelTemplate!.item.relativePath,
        printer!.item,
        copies!
      );
      props.onRequestClose();
    });
  };

  const handleSelectLabelTemplate = (newLabelTemplate: BartenderServerLabelTemplate): void => {
    if (printer != null && newLabelTemplate.bartenderServer.url !== printer.bartenderServer.url) {
      setPrinter(null);
    }
    setLabelTemplate(newLabelTemplate);
  };

  const handleSelectPrinter = (newPrinter: BartenderServerPrinter): void => {
    if (labelTemplate != null && labelTemplate.bartenderServer.url !== newPrinter.bartenderServer.url) {
      setLabelTemplate(null);
    }
    setPrinter(newPrinter);
  };

  const barcodes = props.printableItems.map((item) => item.barcodeOrName);

  return (
    <Modal
      title="Print labels"
      buttons={[
        {
          submit: true,
          style: ButtonStyle.PRIMARY,
          text: isPrinting ? 'Printing...' : 'Print',
          disabled:
            isPrinting ||
            labelTemplate == null ||
            printer == null ||
            copies == null ||
            !selectedItemIds.length,
          dataTestAttrs: {component: 'BartenderPrintModal', key: ModalButtonKey.DONE},
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={print}
      expandHeight={true}
    >
      <div>
        {props.shouldAlwaysShowSampleStatusTable && props.samples ? (
          <SampleStatusTable
            samples={props.samples}
            allowSelection={true}
            selectedSampleIds={selectedItemIds}
            setSelectedSampleIds={setSelectedItemIds}
            canSelectMultiple={true}
          />
        ) : (
          <>
            <FieldLabel label="Barcodes:" />
            <span className="u-small-spacing-left">{barcodes.join(', ')}</span>
          </>
        )}
        <FormGroup>
          <Row>
            <Col md={12}>
              <FieldLabel className="u-small-spacing-top" isRequired={true} label="Label Template" />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <BartenderLabelTemplateDropdown
                bartenderServers={props.labelPrintingConfig.bartenderServers}
                defaultSelectedLabelTemplate={
                  preferences.get(
                    `${PrintPreferences.BARTENDER_LABEL_PRINTING_OPTIONS_NAMESPACE}.${props.printableItems[0].schemaId}`
                  )?.labelTemplate
                }
                selectedLabelTemplate={labelTemplate}
                onSelectLabelTemplate={handleSelectLabelTemplate}
                limitOptionsToServer={
                  labelTemplate == null && printer != null
                    ? {
                        bartenderServerUrl: printer.bartenderServer.url,
                        tooltip:
                          'You must select a label template from the same BarTender server as the selected printer',
                      }
                    : null
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FieldLabel className="u-small-spacing-top" isRequired={true} label="Printer" />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <BartenderPrinterDropdown
                bartenderServers={props.labelPrintingConfig.bartenderServers}
                defaultSelectedPrinter={
                  preferences.get(
                    `${PrintPreferences.BARTENDER_LABEL_PRINTING_OPTIONS_NAMESPACE}.${props.printableItems[0].schemaId}`
                  )?.printer
                }
                selectedPrinter={printer}
                onSelectPrinter={handleSelectPrinter}
                limitOptionsToServer={
                  printer == null && labelTemplate != null
                    ? {
                        bartenderServerUrl: labelTemplate.bartenderServer.url,
                        tooltip:
                          'You must select a printer from the same BarTender server as the selected label template',
                      }
                    : null
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FieldLabel
                className="u-small-spacing-top"
                isRequired={true}
                label="Number of copies per label"
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <NumericInput
                min={1}
                required={true}
                onChange={(e: NumericInputChangeEvent) => {
                  setCopies(e.target.numericValue);
                }}
                value={copies}
                validateInteger={true}
              />
            </Col>
          </Row>
        </FormGroup>
        <Row>
          <Col md={12}>
            <FieldLabel className="u-small-spacing-top" label="Preview" />
          </Col>
        </Row>
        <PrintLabelTimezonePicker />
        <Row>
          <Col md={12}>
            {labelTemplate != null ? (
              <BartenderPrintPreview
                bartenderServer={labelTemplate.bartenderServer}
                labelTemplate={labelTemplate.item}
                labelParams={props.printableItems[0].labelParamsJson}
              />
            ) : (
              'Select a label template to preview'
            )}
          </Col>
        </Row>
      </div>
    </Modal>
  );
}
